import React from 'react';
import { useNavigate } from 'react-router-dom';
import NumberBox from 'devextreme-react/number-box';
import Button from 'devextreme-react/button';
import TagBox from 'devextreme-react/tag-box';
import DataGrid, { Column, FilterRow, Scrolling, SearchPanel, HeaderFilter } from 'devextreme-react/data-grid';
import { SPSyncGrantInfo, SPSyncResult, SPSyncRow } from '../../../api/v2/SPSyncApi';
import { statusCellRender, differencesCellRender } from './SPSyncShared';
import SPSyncSummaryBar from './SPSyncSummaryBar';

interface SPSyncViewProps {
    selectedFY: number;
    setSelectedFY: (fy: number) => void;
    grantList: SPSyncGrantInfo[];
    selectedGrants: string[];
    setSelectedGrants: (grants: string[]) => void;
    result: SPSyncResult | null;
    loadingGrants: boolean;
    loadingReconcile: boolean;
    showOnlyDiffs: boolean;
    setShowOnlyDiffs: (val: boolean) => void;
    loadGrants: () => void;
    runReconcile: () => void;
    filteredRows: SPSyncRow[];
    selectAllGrants: () => void;
    clearGrantSelection: () => void;
}

const SPSyncView: React.FC<SPSyncViewProps> = ({
    selectedFY, setSelectedFY,
    grantList, selectedGrants, setSelectedGrants,
    result,
    loadingGrants, loadingReconcile,
    showOnlyDiffs, setShowOnlyDiffs,
    loadGrants, runReconcile,
    filteredRows,
    selectAllGrants, clearGrantSelection
}) => {
    const navigate = useNavigate();

    const nameCellRender = (cellData: any) => {
        if (cellData.data.dB_EntityId) {
            return (
                <span
                    style={{ color: 'rgb(3, 102, 214)', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => navigate(`/spSync/${cellData.data.dB_EntityId}`)}
                >
                    {cellData.value}
                </span>
            );
        }
        return <span>{cellData.value}</span>;
    };

    const grantRows = filteredRows.filter(r => r.entityType === 'Grant');

    const grantOptions = grantList.map(g => ({
        value: g.baseGrantNumber,
        label: `${g.grantNumber} - ${g.shortInstitution || '(no institution)'}`
    }));

    return (
        <div className="container-fluid" style={{ padding: '20px' }}>
            <h4>SharePoint / DB Reconciliation</h4>

            {/* FY selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <label><strong>Fiscal Year:</strong></label>
                <NumberBox
                    value={selectedFY}
                    onValueChanged={(e) => setSelectedFY(e.value)}
                    width={100}
                    min={2020}
                    max={2040}
                    showSpinButtons={true}
                />
                <Button
                    text="Load Grants"
                    type="default"
                    onClick={loadGrants}
                    disabled={loadingGrants}
                />
                {loadingGrants && <span>Loading grant list...</span>}
            </div>

            {/* Grant selection */}
            {grantList.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <label><strong>Select Grants:</strong></label>
                        <Button text="Select All" type="normal" stylingMode="outlined" onClick={selectAllGrants} />
                        <Button text="Clear" type="normal" stylingMode="outlined" onClick={clearGrantSelection} />
                        <span style={{ color: '#666' }}>({selectedGrants.length} of {grantList.length} selected)</span>
                    </div>
                    <TagBox
                        items={grantOptions}
                        value={selectedGrants}
                        valueExpr="value"
                        displayExpr="label"
                        onValueChanged={(e) => setSelectedGrants(e.value)}
                        searchEnabled={true}
                        maxDisplayedTags={10}
                        showMultiTagOnly={false}
                        width="100%"
                    />
                    <div style={{ marginTop: '10px' }}>
                        <Button
                            text="Reconcile Selected"
                            type="success"
                            onClick={runReconcile}
                            disabled={loadingReconcile || selectedGrants.length === 0}
                        />
                    </div>
                </div>
            )}

            {/* Loading indicator */}
            {loadingReconcile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '40px 0', color: '#666' }}>
                    <img src='Loading.gif' alt='Loading...' style={{ height: '30px', width: '30px' }} />
                    <span style={{ fontSize: '12pt' }}>Reconciling... this may take a while for many grants.</span>
                </div>
            )}

            {/* Results */}
            {!loadingReconcile && result && (
                <>
                    <SPSyncSummaryBar result={result} showOnlyDiffs={showOnlyDiffs} setShowOnlyDiffs={setShowOnlyDiffs} />

                    {result.errors.length > 0 && (
                        <div style={{ color: 'red', marginBottom: '10px', border: '1px solid #dc3545', padding: '8px', borderRadius: '4px' }}>
                            <strong>Errors:</strong>
                            <ul style={{ margin: '4px 0 0 0' }}>{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                        </div>
                    )}

                    <DataGrid
                        dataSource={grantRows}
                        keyExpr="id"
                        showBorders={true}
                        showColumnLines={false}
                        columnAutoWidth={true}
                        wordWrapEnabled={true}
                        showRowLines={true}
                        rowAlternationEnabled={true}
                        customizeColumns={(columns: any[]) => {
                            columns.forEach((col: any) => { col.cssClass = 'vertical-center'; });
                        }}
                    >
                        <SearchPanel visible={true} width={300} placeholder="Search..." />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />
                        <Scrolling mode="virtual" />
                        <Column dataField="name" caption="Grant" cellRender={nameCellRender} minWidth={250} />
                        <Column dataField="status" caption="Status" width={100} cellRender={statusCellRender}>
                            <HeaderFilter allowSearch={true} />
                        </Column>
                        <Column dataField="dB_SPId" caption="DB SPId" width={280} visible={true} />
                        <Column dataField="sP_UniqueId" caption="SP UniqueId" width={280} visible={true} />
                        <Column dataField="dB_SPPath" caption="DB SPPath" visible={false} />
                        <Column dataField="sP_Path" caption="SP Path" visible={false} />
                        <Column dataField="sP_FolderName" caption="SP Folder" visible={false} />
                        <Column dataField="differences" caption="Differences" cellRender={differencesCellRender} minWidth={200} />
                    </DataGrid>
                </>
            )}
        </div>
    );
};

export default SPSyncView;
